import React, { useEffect } from "react";
import "../customCSS/MobileDrawer.css";
import { sanitizeForUrl } from "../utils/urlSanitizer";

interface MenuItem {
  label: string;
  url: string;
}

interface MenuColumn {
  heading: string;
  items: MenuItem[];
}

interface MenuSection {
  id: string;
  label: string;
  columns: MenuColumn[];
  viewAllUrl?: string;
}

interface MobileDrawerProps {
  open: boolean;
  onClose: () => void;
  menuData: unknown;
  onNavigate?: (path: string) => void;
  /** Pass true while menuData is being fetched to show a loading skeleton. */
  loading?: boolean;
}

/**
 * Placeholder content shown in place of the category sections while
 * `loading` is true. Shape roughly mirrors a real section (a title bar
 * plus several item bars) so there's no layout jump once data arrives.
 */
const DrawerSkeleton = () => (
  <div aria-hidden="true">
    {[0, 1].map((section) => (
      <div key={section} className="drawer-section">
        <div className="skeleton skeleton-title" />
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="skeleton skeleton-item" />
        ))}
      </div>
    ))}
  </div>
);

/**
 * Normalizes two possible shapes of menuData into a flat list of sections:
 *  1. An array of categories -> single "CATEGORIES" section.
 *  2. An object keyed by section name (e.g. "Men", "Women"), each with
 *     a `categories` array of { title, items } -> one section per key.
 */
const transformMenuData = (data: unknown): MenuSection[] => {
  if (!data) return [];

  if (Array.isArray(data)) {
    return [
      {
        id: "0",
        label: "CATEGORIES",
        columns: [
          {
            heading: "Categories",
            items: data.map((c: any) => {
              const label = c?.name ?? c?.title ?? String(c);
              return {
                label,
                url: `/search/category/${sanitizeForUrl(label)}?category=${encodeURIComponent(label)}`,
              };
            }),
          },
        ],
      },
    ];
  }

  if (typeof data === "object") {
    return Object.keys(data as Record<string, any>).map((key, index) => {
      const section = (data as Record<string, any>)[key];
      const categories = Array.isArray(section?.categories) ? section.categories : [];

      return {
        id: String(index),
        label: key,
        columns: categories.map((cat: any) => ({
          heading: cat?.title ?? "",
          items: Array.isArray(cat?.items)
            ? cat.items.map((item: any) =>
                typeof item === "string"
                  ? { label: item, url: "#" }
                  : { label: item?.label ?? String(item), url: item?.url ?? "#" }
              )
            : [],
        })),
        viewAllUrl: section?.viewAllUrl,
      };
    });
  }

  return [];
};

export default function MobileDrawer({ open, onClose, menuData, onNavigate, loading = false }: MobileDrawerProps) {
  // Lock background scroll when the drawer is open.
  // Plain `overflow: hidden` on the body isn't enough on iOS/Android -
  // touch gestures can still scroll the page behind a fixed overlay.
  // Pinning the body with position:fixed blocks that reliably, and we
  // restore the exact scroll position on close.
  useEffect(() => {
    if (!open) return;

    const scrollY = window.scrollY;
    const { body } = document;

    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";

    return () => {
      body.style.position = "";
      body.style.top = "";
      body.style.left = "";
      body.style.right = "";
      body.style.width = "";
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  if (!open) return null;

  const sections = loading ? [] : transformMenuData(menuData);

  const handleNavigate = (path: string) => (e: React.MouseEvent) => {
    if (!onNavigate) return;
    e.preventDefault();
    onNavigate(path);
    onClose();
  };

  return (
    <>
      <div className="mobile-backdrop show" onClick={onClose} />

      <aside
        className="mobile-drawer open"
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        <div className="drawer-header d-flex align-items-center justify-content-end px-3 py-2 border-bottom">
          <button className="btn btn-sm btn-outline-secondary" onClick={onClose} aria-label="Close menu">
            ✕
          </button>
        </div>

        <div className="drawer-body p-3" aria-busy={loading}>
          <a
            href="/login"
            className="d-block mb-3 btn btn-outline-danger w-100"
            onClick={handleNavigate("/login")}
          >
            Login / Signup
          </a>

          <a
            href="/orders"
            className="d-block mb-3 text-dark text-decoration-none p-2"
            onClick={handleNavigate("/orders")}
          >
            Orders
          </a>

          {loading && (
            <>
              <span className="visually-hidden">Loading categories…</span>
              <DrawerSkeleton />
            </>
          )}

          {!loading && sections.map((section) => (
            <div key={section.id} className="drawer-section">
              <h6 className="drawer-section-title">{section.label}</h6>

              {section.columns.map((col, cIdx) => (
                <div key={cIdx} className="drawer-column mb-2">
                  {col.heading && col.heading.toLowerCase() !== section.label.toLowerCase() && (
                    <h6 className="drawer-column-title">{col.heading}</h6>
                  )}
                  <ul className="list-unstyled ps-0">
                    {col.items.map((it, i) => (
                      <li key={i}>
                        <a
                          href={it.url}
                          className="text-dark d-block py-1 drawer-item"
                          onClick={handleNavigate(it.url)}
                        >
                          {it.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              {section.viewAllUrl && (
                <a
                  href={section.viewAllUrl}
                  className="fw-bold text-danger d-inline-block mb-2"
                  onClick={handleNavigate(section.viewAllUrl as string)}
                >
                  VIEW ALL
                </a>
              )}
            </div>
          ))}
        </div>
      </aside>
    </>
  );
}